import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/base/SEOHead';
import { useState } from 'react';

export default function About() {
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "Sobre AULA K9 - Don Bandi El Taller de Pensar",
    "description": "Conoce la historia, valores y equipo de AULA K9. Centro de referencia en formación profesional de adiestramiento canino con más de 45 años de experiencia.",
    "mainEntity": {
      "@type": "EducationalOrganization",
      "name": "AULA K9 - Don Bandi El Taller de Pensar",
      "founder": {
        "@type": "Person",
        "name": "Don Bandi",
        "jobTitle": "Fundador y Director",
        "description": "Certificado como Formador de Formadores y Evaluador de futuros profesionales en el adiestramiento canino. Con más de 45 años de experiencia."
      },
      "employee": [
        {
          "@type": "Person",
          "name": "Elena Rodríguez",
          "jobTitle": "Coordinadora Académica"
        },
        {
          "@type": "Person",
          "name": "Miguel Ángel Torres",
          "jobTitle": "Instructor Principal"
        },
        {
          "@type": "Person",
          "name": "Carmen Jiménez",
          "jobTitle": "Especialista en Primeros Auxilios"
        }
      ]
    }
  };

  const team = [
    {
      name: 'Don Bandi',
      role: 'Fundador y Director',
      image: 'https://static.readdy.ai/image/1641cce445e23a33732a9af05560332a/4b755a8beaf3238abeae556b320a2b38.png',
      description: '🐾🐾 Comprometido con la excelencia en formación de calidad. Certificado como Formador de Formadores y Evaluador de futuros profesionales en el adiestramiento canino. Con más de 45 años de experiencia, he ayudado a familias y a sus perros a mejorar su calidad de vida. Mi enfoque se basa en el adiestramiento canino práctico y en una educación colaborativa, trabajando juntos para lograr los mejores resultados.'
    },
    {
      name: 'Elena Rodríguez',
      role: 'Coordinadora Académica',
      image: 'https://public.readdy.ai/ai/img_res/b1f9fb02ea7ad1376dcfe93572ad896f.jpg',
      description: 'Especialista en diseño curricular y metodologías de enseñanza online. Licenciada en Veterinaria con máster en Educación.'
    },
    {
      name: 'Miguel Ángel Torres',
      role: 'Instructor Principal',
      image: 'https://readdy.ai/api/search-image?query=Professional%20spanish%20man%20dog%20trainer%20instructor%20portrait%20smiling%2C%20athletic%20professional%20appearance%2C%20clean%20simple%20background%2C%20natural%20lighting%2C%20professional%20headshot%20photography&width=400&height=400&seq=team-003&orientation=squarish',
      description: 'Experto en modificación de conductas y técnicas de aprendizaje. Certificado en comportamiento animal y bienestar canino.'
    },
    {
      name: 'Carmen Jiménez',
      role: 'Especialista en Primeros Auxilios',
      image: 'https://readdy.ai/api/search-image?query=Professional%20spanish%20woman%20veterinary%20specialist%20portrait%20smiling%2C%20caring%20professional%20appearance%20with%20medical%20background%2C%20clean%20simple%20background%2C%20natural%20lighting%2C%20professional%20headshot%20photography&width=400&height=400&seq=team-004&orientation=squarish',
      description: 'Veterinaria especializada en urgencias y cuidados intensivos. Formadora certificada en primeros auxilios veterinarios.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Sobre Nosotros | AULA K9 - Don Bandi El Taller de Pensar | Historia y Equipo"
        description="Conoce AULA K9 - Don Bandi El Taller de Pensar. Centro de referencia en formación profesional de adiestramiento canino con más de 45 años de experiencia. Descubre nuestra historia, valores y equipo de profesionales certificados comprometidos con la excelencia educativa y el bienestar animal."
        keywords="AULA K9, Don Bandi, equipo profesional, historia adiestramiento canino, valores educativos, formación colaborativa, bienestar animal, excelencia educativa"
        canonical="/nosotros"
        schema={schema}
      />
      <Header />
      <main>
        <section className="bg-gradient-to-br from-teal-600 to-teal-800 text-white py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl">
              <h1 className="text-5xl font-bold mb-6">
                Sobre AULA K9
              </h1>
              <p className="text-xl text-teal-50 leading-relaxed">
                Don Bandi "El Taller de Pensar" nace de la pasión por los perros y transmitir nuestros conocimientos en la educación canina, el adiestramiento y el compromiso con la excelencia en la formación profesional. Somos un centro de referencia en adiestramiento canino certificado.
              </p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
              <div>
                <h2 className="text-4xl font-bold text-gray-900 mb-6">
                  Nuestra Historia
                </h2>
                <div className="space-y-4 text-gray-700 leading-relaxed">
                  <p>
                    AULA K9 nace del sueño de Don Bandi de la necesidad de compartir nuestra experiencia y crear un espacio de formación profesional que combine la excelencia académica con el amor y el respeto por los animales. Con más de cuatro décadas de experiencia en el sector, decidimos crear una plataforma educativa que democratizara el acceso a una formación de calidad en adiestramiento canino.
                  </p>
                  <p>
                    Nuestro nombre, "El Taller de Pensar", refleja nuestra filosofía: no solo enseñamos técnicas, sino que formamos profesionales capaces de pensar críticamente, analizar situaciones y tomar decisiones fundamentadas en el bienestar animal y la ciencia del comportamiento.
                  </p>
                  <p>
                    Hoy, somos un centro de referencia en formación online de adiestramiento canino, con certificación oficial según el RD 545/2023. Hemos formado a más de 500 profesionales que trabajan exitosamente en toda España y Europa, contribuyendo a elevar los estándares del sector.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20professional%20dog%20training%20academy%20building%20exterior%20with%20dogs%20and%20trainers%2C%20welcoming%20educational%20facility%20atmosphere%2C%20clean%20simple%20background%2C%20bright%20natural%20lighting%2C%20professional%20architectural%20photography%20showing%20quality%20and%20professionalism&width=800&height=600&seq=about-001&orientation=landscape"
                  alt="AULA K9"
                  className="rounded-2xl shadow-2xl object-cover object-top w-full h-[500px]"
                />
              </div>
            </div>

            {/* Nuestra Cultura */}
            <div className="mb-20">
              <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
                Nuestra Cultura
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed text-center max-w-4xl mx-auto mb-12">
                Aula K9 Don Bandi "El Taller de Pensar" nace como un espacio de formación online comprometido con la educación canina profesional y responsable. Creemos que la base de cualquier intervención es el conocimiento, la ética y el respeto por el perro como individuo.
              </p>

              {/* Nuestros Tres Pilares - Desplegables */}
              <div className="max-w-4xl mx-auto space-y-4">
                {/* Misión */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  <button
                    onClick={() => toggleSection('mision')}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                        <i className="ri-compass-3-fill text-teal-600 text-2xl"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Misión</h3>
                    </div>
                    <i className={`ri-arrow-${openSection === 'mision' ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                  </button>
                  {openSection === 'mision' && (
                    <div className="px-8 pb-8 pt-4 bg-gray-50 border-t border-gray-100">
                      <p className="text-gray-700 leading-relaxed mb-4">
                        La misión de Aula K9 Don Bandi "El Taller de Pensar" es ayudar a los profesionales y propietarios a satisfacer las necesidades de sus perros a través de la información, formación y el asesoramiento. Nuestro propósito central es fomentar una convivencia armoniosa a través de una educación canina profesional, para la prevención de futuros problemas de conducta y fortaleciendo la relación entre el humano y el perro.
                      </p>
                      <p className="text-gray-700 leading-relaxed font-semibold">
                        Mejorar la convivencia y el bienestar de los perros y sus familias mediante información, formación, asesoramiento, educación y adiestramiento profesional en positivo.
                      </p>
                      <p className="text-gray-600 leading-relaxed mt-3 italic">
                        <strong>Cómo:</strong> Educación respetuosa, métodos basados en evidencia, acompañamiento al guía y adaptación a cada perro y hogar.
                      </p>
                    </div>
                  )}
                </div>

                {/* Visión */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  <button
                    onClick={() => toggleSection('vision')}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                        <i className="ri-eye-fill text-teal-600 text-2xl"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Visión</h3>
                    </div>
                    <i className={`ri-arrow-${openSection === 'vision' ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                  </button>
                  {openSection === 'vision' && (
                    <div className="px-8 pb-8 pt-4 bg-gray-50 border-t border-gray-100">
                      <p className="text-gray-700 leading-relaxed mb-4">
                        Nuestra visión se enfoca en ser un referente en la educación y el adiestramiento sin violencia y en la profesionalización del sector. Buscamos extender el conocimiento y la afición por las cualidades caninas, logrando que los perros sean mejor recibidos en la sociedad por ser animales educados, funcionales, respetuosos y equilibrados. Aspiramos a que cada perro esté "en buenas manos" mediante una metodología consolidada y ética.
                      </p>
                      <p className="text-gray-700 leading-relaxed font-semibold">
                        Ser un referente en Navarra en educación y formación canina, reconocido por la calidad profesional y el trato ético al animal.
                      </p>
                      <p className="text-gray-600 leading-relaxed mt-3 italic">
                        <strong>El impacto a futuro:</strong> Contribuir a una sociedad más responsable con los animales, donde el perro sea un miembro integrado y equilibrado en la familia y la comunidad.
                      </p>
                    </div>
                  )}
                </div>

                {/* Valores */}
                <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100">
                  <button
                    onClick={() => toggleSection('valores')}
                    className="w-full px-8 py-6 flex items-center justify-between hover:bg-gray-50 transition-all cursor-pointer"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-teal-100 rounded-xl flex items-center justify-center">
                        <i className="ri-star-fill text-teal-600 text-2xl"></i>
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">Valores</h3>
                    </div>
                    <i className={`ri-arrow-${openSection === 'valores' ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                  </button>
                  {openSection === 'valores' && (
                    <div className="px-8 pb-8 pt-4 bg-gray-50 border-t border-gray-100">
                      <p className="text-gray-700 leading-relaxed mb-6">
                        La cultura de Aula K9 Don Bandi "El Taller de Pensar" como organización se asienta en los siguientes valores fundamentales:
                      </p>
                      <div className="space-y-4">
                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-heart-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Bienestar y Respeto</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Prioridad al bienestar físico y emocional del perro, evitando métodos aversivos y buscando siempre su seguridad y confianza. Es el eje prioritario. Todas las técnicas están orientadas a la salud, el confort y el respeto al perro.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-shield-check-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Adiestramiento No Violento</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Compromiso absoluto con métodos exentos de violencia y el uso de refuerzos positivos para generar seguridad y confianza.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-graduation-cap-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Profesionalidad y Formación Continua</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Trabajo acreditado, actualizado y basado en conocimientos técnicos y experiencia. Valoración del conocimiento técnico acreditado y la actualización constante de nuestros técnicos instructores formadores.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-team-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Empatía y Pedagogía con las Personas</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Enseñar al humano a comprender y entender al perro, comunicarse mejor y gestionar el día a día con claridad y paciencia.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-hand-heart-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Pasión y Vocación</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Entendemos el trabajo como una "forma de vida" basada en la ayuda a los demás (tanto humanos como perros).
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-links-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Apego/Vínculo (El Gran Apego/Vínculo)</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Creencia en que la conexión, el apego/vínculo y la comunicación no verbal son la base de cualquier entrenamiento exitoso.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-global-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Responsabilidad Social</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Promover la tenencia responsable, la prevención de problemas de conducta y la integración segura del perro en entornos urbanos.
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-4">
                          <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <i className="ri-price-tag-3-fill text-teal-600 text-xl"></i>
                          </div>
                          <div>
                            <h4 className="font-bold text-gray-900 mb-1">Accesibilidad</h4>
                            <p className="text-gray-600 leading-relaxed">
                              Defendemos que una formación de alta calidad debe ser compatible con un precio asequible para los dueños.
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-6 pt-6 border-t border-gray-200">
                        <p className="text-gray-700 leading-relaxed font-semibold italic">
                          En resumen, nuestra cultura se resume en nuestro lema de trabajo: <strong>Planificación, paciencia, perseverancia y trabajo, siempre bajo el paraguas del respeto animal.</strong>
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Equipo de Expertos */}
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
                Equipo de Expertos
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                {team.map((member, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all"
                  >
                    <img
                      src={member.image}
                      alt={member.name}
                      className="w-full h-64 object-cover object-top"
                    />
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {member.name}
                      </h3>
                      <p className="text-teal-600 font-semibold mb-3">
                        {member.role}
                      </p>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {member.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="bg-gradient-to-br from-teal-600 to-teal-800 rounded-3xl p-12 text-white text-center">
              <h2 className="text-4xl font-bold mb-6">
                Únete a Nuestra Comunidad
              </h2>
              <p className="text-xl text-teal-50 mb-8 max-w-3xl mx-auto">
                Forma parte de una comunidad de profesionales comprometidos con la excelencia en el adiestramiento canino. Juntos, elevamos los estándares del sector.
              </p>
              <button className="rounded-full bg-white px-8 py-4 text-base font-semibold text-teal-600 shadow-lg hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer">
                Comenzar Ahora
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
